import { useState } from 'react';
import api from '../services/api';
import { X, RotateCcw, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function RefundModal({ sale, onClose, onRefunded, currencySymbol = '$' }) {
  const [refundItems, setRefundItems] = useState(
    sale.items.map(item => ({
      ...item,
      quantityToRefund: 0
    }))
  );
  const [reason, setReason] = useState('');
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleQuantityChange = (itemId, val) => {
    setRefundItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const max = item.quantity - item.refundedQuantity;
        const quantity = Math.min(max, Math.max(0, parseInt(val) || 0));
        return { ...item, quantityToRefund: quantity };
      }
      return item;
    }));
  };

  const totalRefundAmount = isCustomAmount 
    ? (parseFloat(overrideAmount) || 0)
    : refundItems.reduce((acc, item) => {
        return acc + (item.price * item.quantityToRefund);
      }, 0) * (1 + (sale.tax / (sale.total - sale.tax - sale.discount || 1))); // Proportional tax

  const handleSubmit = async (e) => {
    e.preventDefault();
    const itemsToRefund = refundItems.filter(i => i.quantityToRefund > 0).map(i => ({
      productId: i.productId,
      variantId: i.variantId,
      quantity: i.quantityToRefund
    }));

    if (itemsToRefund.length === 0) return setError('Please select items to refund');

    setIsProcessing(true);
    setError('');
    try {
      await api.post(`/sales/${sale.id}/refund`, { 
        items: itemsToRefund, 
        reason,
        totalAmount: isCustomAmount ? totalRefundAmount : undefined
      });
      setSuccess(true);
      setTimeout(() => {
        onRefunded();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Refund failed');
    } finally {
      setIsProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-theme-card p-12 rounded-3xl text-center flex flex-col items-center shadow-2xl">
          <div className="w-20 h-20 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 size={48} />
          </div>
          <h2 className="text-2xl font-black text-theme-main uppercase tracking-tight">Refund Successful</h2>
          <p className="text-theme-muted mt-2">Inventory has been restocked and logs updated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-theme-card w-full max-w-2xl rounded-2xl shadow-2xl border border-theme overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand/10 text-brand rounded-lg"><RotateCcw size={20} /></div>
            <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Process Refund: Sale #{sale.id}</h3>
          </div>
          <button onClick={onClose} className="text-theme-muted hover:text-theme-main transition-colors"><X size={24} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 flex-1 overflow-y-auto space-y-6">
            {error && (
              <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl flex items-center gap-3 text-sm font-bold border border-rose-500/20">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-12 gap-4 text-[10px] font-black text-theme-muted uppercase tracking-widest px-4">
                <div className="col-span-6">Product</div>
                <div className="col-span-2 text-center">Purchased</div>
                <div className="col-span-2 text-center">Refunded</div>
                <div className="col-span-2 text-center">Qty to Refund</div>
              </div>

              {refundItems.map(item => (
                <div key={item.id} className="grid grid-cols-12 gap-4 items-center bg-theme-main/10 p-4 rounded-xl border border-theme">
                  <div className="col-span-6">
                    <p className="font-bold text-theme-main text-sm">{item.product.name}</p>
                    <p className="text-[10px] text-theme-muted font-mono">{currencySymbol}{item.price.toFixed(2)} ea</p>
                  </div>
                  <div className="col-span-2 text-center font-bold text-theme-main">{item.quantity}</div>
                  <div className="col-span-2 text-center font-bold text-rose-500">{item.refundedQuantity}</div>
                  <div className="col-span-2">
                    <input 
                      type="number"
                      min="0"
                      max={item.quantity - item.refundedQuantity}
                      value={item.quantityToRefund}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                      className="w-full bg-theme-main border border-theme rounded-lg py-1.5 px-2 text-center text-theme-main font-bold focus:ring-2 focus:ring-brand outline-none"
                    />
                    <p className="text-[8px] text-center text-theme-muted mt-1 uppercase font-black">Max {item.quantity - item.refundedQuantity}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded-xl flex items-start gap-3">
              <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[10px] font-bold text-theme-muted leading-relaxed uppercase tracking-tight">
                To refund a specific dollar amount (e.g. for damage or goodwill), select the items first, then use the <span className="text-amber-500 font-black">Total Control</span> panel on the right.
              </p>
            </div>

            <div className="flex gap-6">
              <div className="flex-1">
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Reason for Refund</label>
                <textarea 
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  className="w-full bg-theme-main border border-theme rounded-xl py-3 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all h-24 resize-none"
                  placeholder="Defective, Customer change of mind, etc..."
                ></textarea>
              </div>
              <div className="w-56">
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Manual Total Override</label>
                <div className={`p-4 rounded-2xl border transition-all ${isCustomAmount ? 'bg-amber-500/10 border-amber-500/50' : 'bg-theme-main border-theme'}`}>
                  <button 
                    type="button"
                    onClick={() => {
                      setIsCustomAmount(!isCustomAmount);
                      if (!isCustomAmount) setOverrideAmount(totalRefundAmount.toFixed(2));
                    }}
                    className={`w-full py-2.5 px-4 rounded-xl text-[10px] font-black uppercase tracking-[0.1em] transition-all mb-3 ${isCustomAmount ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20' : 'bg-theme-card text-theme-muted border border-theme hover:bg-theme-main'}`}
                  >
                    {isCustomAmount ? 'Manual Mode Active' : 'Enable Manual Total'}
                  </button>
                  {isCustomAmount ? (
                    <div className="space-y-2 animate-fade-in">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500 font-black">{currencySymbol}</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={overrideAmount}
                          onChange={e => setOverrideAmount(e.target.value)}
                          className="w-full bg-theme-main border border-amber-500/30 rounded-xl py-2.5 pl-7 pr-3 text-theme-main font-black focus:ring-2 focus:ring-amber-500 outline-none text-sm"
                          placeholder="0.00"
                          autoFocus
                        />
                      </div>
                      <p className="text-[9px] font-bold text-amber-600 uppercase text-center">Type exact amount above</p>
                    </div>
                  ) : (
                    <p className="text-[9px] font-bold text-theme-muted uppercase text-center leading-relaxed">
                      Click button to type a custom refund amount.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-theme-main/10 border-t border-theme flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black text-theme-muted uppercase tracking-widest leading-none">Total Refund Amount</p>
              <p className={`text-2xl font-black ${isCustomAmount ? 'text-amber-500' : 'text-rose-500'}`}>
                {currencySymbol}{totalRefundAmount.toFixed(2)}
              </p>
            </div>
            <div className="flex gap-4">
              <button 
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-theme-main text-theme-main border border-theme rounded-xl font-black transition-all hover:bg-theme-main/80 uppercase tracking-widest text-xs"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isProcessing}
                className="bg-brand hover:bg-brand-light text-white py-3 px-8 rounded-xl font-black transition-all shadow-lg flex items-center justify-center gap-2 uppercase tracking-widest text-xs disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
